import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  parseISO,
} from "date-fns";
import { es } from "date-fns/locale";
import {
  Calendar as CalendarIcon,
  Clock4,
  MapPin,
  StickyNote,
  ChevronLeft,
  ChevronRight,
  Copy,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

const VIEW_MODES = [
  { value: "month", label: "Mes" },
  { value: "week", label: "Semana" },
  { value: "day", label: "Día" },
];

const WEEK_LABELS = ["L", "M", "X", "J", "V", "S", "D"];

const weekOptions = { weekStartsOn: 1 };

const timeToMinutes = (time) => {
  if (!time) return 24 * 60;
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + (minutes || 0);
};

const formatTimeRange = (start, end) => {
  if (!start && !end) return "Horario por definir";
  if (start && end) return `${start} - ${end}`;
  return start || end || "Horario por definir";
};

const safeParseDate = (value) => {
  if (!value) return null;
  const parsed = parseISO(value);
  return Number.isNaN(parsed) ? null : parsed;
};

export default function CalendarioPage() {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("month");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [calendarToken, setCalendarToken] = useState("");
  const [calendarLoading, setCalendarLoading] = useState(false);

  useEffect(() => {
    const loadClientes = async () => {
      try {
        const response = await api.getClientes();
        setClientes(response.data);
      } catch (error) {
        console.error("Error loading clientes:", error);
      } finally {
        setLoading(false);
      }
    };

    loadClientes();
  }, []);

  useEffect(() => {
    loadCalendarToken();
  }, []);

  const loadCalendarToken = async () => {
    try {
      console.log('DEBUG: Cargando calendar token...');
      const response = await api.getCalendarToken();
      console.log('DEBUG: Response:', response);
      setCalendarToken(response.data.calendar_token);
      console.log('DEBUG: Token set:', response.data.calendar_token);
    } catch (error) {
      console.error("Error cargando calendar token:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Error desconocido";
      toast.error(`Error cargando token: ${errorMessage}`);
    }
  };

  const generateNewToken = async () => {
    setCalendarLoading(true);
    try {
      console.log('DEBUG: Generando nuevo calendar token...');
      const response = await api.generateCalendarToken();
      console.log('DEBUG: Generate response:', response);
      setCalendarToken(response.data.calendar_token);
      console.log('DEBUG: New token set:', response.data.calendar_token);
      toast.success("Token de calendario regenerado");
    } catch (error) {
      console.error("Error generando token:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Error desconocido";
      toast.error(`Error al generar token: ${errorMessage}`);
    } finally {
      setCalendarLoading(false);
    }
  };

  // URLs del calendario
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
  
  const calendarUrl = calendarToken 
    ? `${backendUrl}/api/calendar/consultas.ics?token=${calendarToken}`
    : "";

  const calendarUrlForiPhone = calendarToken 
    ? `webcal://${backendUrl.replace('http://', '').replace('https://', '')}/api/calendar/consultas.ics?token=${calendarToken}`
    : "";

  const handleCopyUrl = () => {
    if (calendarUrl) {
      navigator.clipboard.writeText(calendarUrl);
      toast.success("URL copiada al portapapeles");
    }
  };

  const handleCopyiPhoneUrl = () => {
    if (calendarUrlForiPhone) {
      navigator.clipboard.writeText(calendarUrlForiPhone);
      toast.success("URL para iPhone copiada al portapapeles");
    }
  };

  const events = useMemo(() => {
    return clientes
      .map((cliente) => {
        const eventDate = safeParseDate(cliente.fecha_evento);
        if (!eventDate) return null;

        return {
          id: cliente.id,
          nombre: cliente.nombre,
          ubicacion: cliente.ubicacion,
          fecha: cliente.fecha_evento,
          date: eventDate,
          hora_inicio: cliente.hora_inicio || "",
          hora_fin: cliente.hora_fin || "",
          notas: cliente.notas,
          link: `/clientes/${cliente.id}`,
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (a.date.getTime() !== b.date.getTime()) {
          return a.date.getTime() - b.date.getTime();
        }
        return timeToMinutes(a.hora_inicio) - timeToMinutes(b.hora_inicio);
      });
  }, [clientes]);

  const eventsByDate = useMemo(() => {
    return events.reduce((acc, event) => {
      const key = format(event.date, "yyyy-MM-dd");
      if (!acc[key]) acc[key] = [];
      acc[key].push(event);
      return acc;
    }, {});
  }, [events]);

  const monthDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentDate), weekOptions);
    const end = endOfWeek(endOfMonth(currentDate), weekOptions);
    const days = [];
    let day = start;

    while (day <= end) {
      days.push(day);
      day = addDays(day, 1);
    }
    return days;
  }, [currentDate]);

  const currentWeekDays = useMemo(() => {
    const start = startOfWeek(currentDate, weekOptions);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [currentDate]);

  const dayEvents = useMemo(() => {
    const key = format(currentDate, "yyyy-MM-dd");
    return eventsByDate[key] || [];
  }, [currentDate, eventsByDate]);

  const handlePrev = () => {
    if (viewMode === "month") {
      setCurrentDate((date) => subMonths(date, 1));
    } else if (viewMode === "week") {
      setCurrentDate((date) => addDays(date, -7));
    } else {
      setCurrentDate((date) => addDays(date, -1));
    }
  };

  const handleNext = () => {
    if (viewMode === "month") {
      setCurrentDate((date) => addMonths(date, 1));
    } else if (viewMode === "week") {
      setCurrentDate((date) => addDays(date, 7));
    } else {
      setCurrentDate((date) => addDays(date, 1));
    }
  };

  const openEvent = (event) => {
    setSelectedEvent(event);
    setDialogOpen(true);
  };

  const renderMonthView = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-7 gap-2 text-center text-xs font-semibold text-muted-foreground">
        {WEEK_LABELS.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-2">
        {monthDays.map((day) => {
          const key = format(day, "yyyy-MM-dd");
          const dayEvents = eventsByDate[key] || [];
          const isToday = isSameDay(day, new Date());
          const inMonth = isSameMonth(day, currentDate);

          return (
            <Card
              key={key}
              className={`border ${
                inMonth ? "bg-white" : "bg-muted"
              } ${isToday ? "border-primary" : "border-border/60"}`}
            >
              <CardContent className="p-2 h-32 flex flex-col">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span
                    className={`font-semibold ${inMonth ? "text-primary" : "text-muted-foreground/60"}`}
                  >
                    {format(day, "d")}
                  </span>
                  <Badge variant="outline" className="text-[10px]">
                    {dayEvents.length}
                  </Badge>
                </div>
                <ScrollArea className="mt-2 space-y-2">
                  {dayEvents.length === 0 && (
                    <p className="text-[11px] text-muted-foreground/70">Sin eventos</p>
                  )}
                  {dayEvents.map((event) => (
                    <button
                      key={event.id + event.hora_inicio}
                      className="w-full text-left text-[11px] bg-primary/10 text-primary rounded-lg p-2 hover:bg-primary/20 transition"
                      onClick={() => openEvent(event)}
                    >
                      <p className="font-semibold truncate">{event.nombre}</p>
                      <p className="text-[10px] text-primary/80">
                        {formatTimeRange(event.hora_inicio, event.hora_fin)}
                      </p>
                    </button>
                  ))}
                </ScrollArea>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );

  const renderWeekView = () => (
    <div className="grid grid-cols-7 gap-3">
      {currentWeekDays.map((day) => {
        const key = format(day, "yyyy-MM-dd");
        const dayEvents = eventsByDate[key] || [];
        const isToday = isSameDay(day, new Date());

        return (
          <Card key={key} className={`border ${isToday ? "border-primary" : "border-border"}`}>
            <CardContent className="p-3 space-y-3">
              <div>
                <p className="text-xs text-muted-foreground">
                  {format(day, "EEEE", { locale: es })}
                </p>
                <p className="text-xl font-heading text-primary">
                  {format(day, "d")}
                </p>
              </div>
              {dayEvents.length === 0 ? (
                <p className="text-sm text-muted-foreground/80">Sin eventos</p>
              ) : (
                <div className="space-y-2">
                  {dayEvents.map((event) => (
                    <button
                      key={event.id + event.hora_inicio}
                      className="w-full text-left rounded-xl border border-primary/20 p-3 hover:border-primary/60 transition"
                      onClick={() => openEvent(event)}
                    >
                      <p className="font-semibold text-primary">{event.nombre}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatTimeRange(event.hora_inicio, event.hora_fin)}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{event.ubicacion}</p>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  const renderDayView = () => (
    <Card className="border border-border/80">
      <CardContent className="p-4 space-y-4">
        <div>
          <p className="text-sm text-muted-foreground">
            {format(currentDate, "EEEE, d 'de' MMMM", { locale: es })}
          </p>
          <p className="text-2xl font-heading text-primary">
            {format(currentDate, "d")}
          </p>
        </div>
        {dayEvents.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            No hay eventos para este día
          </div>
        ) : (
          <div className="space-y-3">
            {dayEvents.map((event) => (
              <button
                key={event.id + event.hora_inicio}
                className="w-full text-left rounded-2xl border border-primary/30 p-4 hover:border-primary transition"
                onClick={() => openEvent(event)}
              >
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-primary">{event.nombre}</p>
                  <Badge variant="secondary">{formatTimeRange(event.hora_inicio, event.hora_fin)}</Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1 truncate">{event.ubicacion}</p>
                {event.notas && (
                  <p className="text-xs text-muted-foreground/80 mt-2 line-clamp-2">
                    {event.notas}
                  </p>
                )}
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-primary font-heading text-xl">Cargando calendario...</div>
      </div>
    );
  }

  return (
    <div data-testid="calendario-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold text-primary flex items-center gap-3">
            <CalendarIcon className="w-8 h-8" />
            Calendario de Eventos
          </h1>
          <p className="text-muted-foreground mt-1">
            Visualiza las reservas confirmadas por mes, semana o día
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-full" onClick={() => setCurrentDate(new Date())}>
            Hoy
          </Button>
          <div className="flex items-center border border-border rounded-full overflow-hidden">
            <button className="p-2 hover:bg-muted" onClick={handlePrev}>
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="px-4 text-sm font-semibold text-primary">
              {format(currentDate, "MMMM yyyy", { locale: es })}
            </div>
            <button className="p-2 hover:bg-muted" onClick={handleNext}>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="flex bg-muted rounded-full p-1">
            {VIEW_MODES.map((mode) => (
              <button
                key={mode.value}
                onClick={() => setViewMode(mode.value)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition ${
                  viewMode === mode.value ? "bg-white shadow text-primary" : "text-muted-foreground"
                }`}
              >
                {mode.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {viewMode === "month" && renderMonthView()}
      {viewMode === "week" && renderWeekView()}
      {viewMode === "day" && renderDayView()}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        {selectedEvent && (
          <DialogContent className="rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-primary text-2xl">{selectedEvent.nombre}</DialogTitle>
              <DialogDescription>
                Evento del {format(selectedEvent.date, "d 'de' MMMM yyyy", { locale: es })}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Clock4 className="w-4 h-4" />
                <span>{formatTimeRange(selectedEvent.hora_inicio, selectedEvent.hora_fin)}</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <MapPin className="w-4 h-4" />
                <span>{selectedEvent.ubicacion || "Sin ubicación especificada"}</span>
              </div>
              <div className="flex items-start gap-3 text-sm text-muted-foreground">
                <StickyNote className="w-4 h-4 mt-0.5" />
                <span>{selectedEvent.notas || "Sin notas adicionales"}</span>
              </div>
            </div>
            <DialogFooter className="flex justify-between">
              <Button variant="ghost" onClick={() => setDialogOpen(false)} className="rounded-full">
                Cerrar
              </Button>
              <Button asChild className="rounded-full">
                <Link to={selectedEvent.link}>Ver cliente</Link>
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>

      {/* Componente de suscripción a calendario */}
      <Card className="mt-6 border-0 shadow-soft">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <CalendarIcon className="w-5 h-5" />
            Sincronizar con iPhone
          </h3>
          
          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-2">
                URL de suscripción (solo lectura):
              </p>
              <div className="flex gap-2">
                <Input 
                  value={calendarUrl}
                  readOnly 
                  className="font-mono text-xs"
                  placeholder="Cargando token..."
                />
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={handleCopyUrl}
                  disabled={!calendarToken}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <Button 
              onClick={() => {
                if (calendarUrlForiPhone) {
                  window.location.href = calendarUrlForiPhone;
                }
              }}
              className="w-full"
              disabled={!calendarToken}
            >
              <CalendarIcon className="w-4 h-4 mr-2" />
              Suscribirse en iPhone
            </Button>

            <Button 
              variant="outline" 
              onClick={generateNewToken}
              className="w-full"
              disabled={calendarLoading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${calendarLoading ? 'animate-spin' : ''}`} />
              {calendarLoading ? 'Generando...' : 'Generar nuevo token'}
            </Button>
          </div>

          <div className="mt-4 space-y-2 text-xs text-muted-foreground bg-blue-50 p-3 rounded-lg">
            <p>• El calendario es de <strong>solo lectura</strong></p>
            <p>• Se actualiza automáticamente cada hora</p>
            <p>• Funciona en iPhone, iPad y Mac</p>
            <p>• No necesitas volver a suscribirte</p>
            <p>• Muestra las mismas consultas que ves aquí</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
